import React, { useRef } from 'react';
import styled from 'styled-components';
import { useField, useFormikContext, ErrorMessage } from 'formik';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import themeColors from '../../assets/styles/themeColors';
import { pxToRem } from '../../assets/styles/helper';
import { InputError } from './Components/InputComponents';
import i18next from '../../i18n';

const Container = styled.div`
  margin-bottom: calc(1rem * var(--legacy-scale, 1));
`;

const InputGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const EmailInput = styled.input`
  flex: 1;
  height: 40px;
  font-size: ${pxToRem(16)};
  padding: 0 12px;
  border: 1px solid ${themeColors.colorInputBorder};
  border-radius: 4px;
`;

const EmailList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: 10px;
`;

const EmailItem = styled.li`
  background: ${themeColors.colorInputBackgroundDisabled};
  padding: 8px 12px;
  border-radius: 4px;
  font-size: ${pxToRem(14)};
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: ${themeColors.colorError};
  font-size: 18px;
  cursor: pointer;
`;

const MultiEmailField = ({ setErrorMessage }) => {
  const inputRef = useRef(null);
  const {
    values,
    setFieldValue,
    setFieldError,
    setFieldTouched,
  } = useFormikContext();
  const [emailsField] = useField('emails');
  const handleAddEmail = () => {
    const trimmed = values.currentEmail.trim();
    if (
      trimmed &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) &&
      !values.emails.includes(trimmed)
    ) {
      const updated = [...values.emails, trimmed];
      setFieldValue('emails', updated);
      setFieldValue('currentEmail', '');
      // eslint-disable-next-line no-unused-expressions
      inputRef.current?.focus();
    } else {
      if (!trimmed) {
        setFieldError(
          'emails',
          i18next.t('validationSchema.general.emailRequired'),
        );
        setFieldTouched('emails', true);
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        setFieldError(
          'emails',
          i18next.t('validationSchema.general.emailRequired'),
        );
        setFieldTouched('emails', true);
        return;
      }

      if (values.emails.includes(trimmed)) {
        setFieldError(
          'emails',
          i18next.t('validationSchema.general.invalidEmailFormat'),
        );
        setFieldTouched('emails', true);
      }
    }
  };
  const handleRemoveEmail = (index) => {
    const updated = [...values.emails];

    setErrorMessage('');
    updated.splice(index, 1);
    setFieldValue('emails', updated);
  };

  return (
    <Container>
      <InputGroup>
        <EmailInput
          ref={inputRef}
          name="currentEmail"
          value={values.currentEmail}
          onChange={(e) => setFieldValue('currentEmail', e.target.value)}
          placeholder="Enter email"
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={handleAddEmail}
          type="button"
        >
          Add
        </Button>
      </InputGroup>
      <ErrorMessage name="emails">
        {(errorMessage) => <InputError>{errorMessage}</InputError>}
      </ErrorMessage>
      <EmailList>
        {emailsField.value.map((email, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <EmailItem key={`email${index}`}>
            {email}
            <RemoveBtn
              type="button"
              onClick={() => handleRemoveEmail(index)}
              aria-label="Remove email"
            >
              ×
            </RemoveBtn>
          </EmailItem>
        ))}
      </EmailList>
    </Container>
  );
};

MultiEmailField.propTypes = {
  setErrorMessage: PropTypes.func,
};

export default MultiEmailField;
